import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import { CardStatus, TxStatus } from '@shared/types';

import { Panel, PanelHeader, SectionHeading } from '@/components/ui/Panel';
import { Figure, Stat } from '@/components/ui/Figure';
import { EmptyState, InlineError } from '@/components/ui/EmptyState';
import { Skeleton, TableSkeleton } from '@/components/ui/Skeleton';
import { VerificationPrompt } from '@/components/VerificationPrompt';
import { TrafficPanel } from '@/components/traffic/TrafficPanel';
import { BalancesPanel } from '@/components/finance/BalancesPanel';
import { ActivityList } from '@/components/ActivityList';
import { useBalance, useKyc, useProfile, useStats, useTransactions } from '@/lib/queries';
import { toMessage } from '@/lib/api';
import { rub, usdt } from '@/lib/format';
import { NOUN, plural } from '@/lib/plural';
import { useAuthStore } from '@/store/auth';

const RECENT_LIMIT = 10;

export default function DashboardPage() {
  const setTrader = useAuthStore((state) => state.setTrader);
  // Subscribed, not read imperatively, so the header updates when it hydrates.
  const cachedEmail = useAuthStore((state) => state.trader?.email);

  const profile = useProfile();
  const balance = useBalance();
  const stats = useStats();
  const kyc = useKyc();
  const transactions = useTransactions({ limit: RECENT_LIMIT });

  // Keep the cached profile in the session store fresh — the rail renders it.
  const profileData = profile.data;
  useEffect(() => {
    if (profileData) setTrader(profileData);
  }, [profileData, setTrader]);

  const recent = transactions.data?.slice(0, RECENT_LIMIT) ?? [];

  // Fall back to counting the loaded page if the stats endpoint is unavailable,
  // so the strip degrades to "approximate" rather than to empty.
  const derived = transactions.data ?? [];
  const pendingCount =
    stats.data?.pendingCount ?? derived.filter((tx) => tx.status === TxStatus.Pending).length;
  const openDisputes =
    stats.data?.openDisputes ?? derived.filter((tx) => tx.hasOpenDispute).length;
  const confirmedToday = stats.data?.confirmedToday ?? countConfirmedToday(derived);
  const verifiedCards =
    stats.data?.verifiedCards ??
    kyc.data?.cards.filter((card) => card.status === CardStatus.Verified).length ??
    0;

  /**
   * The second cell of the balances panel: what the last 24 hours produced.
   *
   * The rouble figure is what passed through the trader's cards; the USDT
   * figure is the part that is theirs. Both are named, because a trader
   * comparing the big number to their bank statement and the small one to their
   * withdrawable balance is doing exactly the right thing.
   */
  const earned = balance.isPending ? (
    <>
      <Skeleton className="h-2.5 w-20" />
      <Skeleton className="mt-4 h-8 w-40" />
      <Skeleton className="mt-3 h-3.5 w-36" />
    </>
  ) : balance.isError ? (
    <InlineError
      message={toMessage(balance.error, 'Итоги за сутки недоступны.')}
      onRetry={() => void balance.refetch()}
    />
  ) : (
    <Figure
      size="md"
      label="Прошло через карты · за 24 часа"
      value={rub(balance.data.earned24hRub)}
      caption={
        <>
          Ваша комиссия за это время —{' '}
          <span className="num">{usdt(balance.data.earned24hUsdt)}</span> ·{' '}
          <span className="num">{balance.data.txCount24h}</span>{' '}
          {plural(balance.data.txCount24h, NOUN.transaction)}
        </>
      }
    />
  );

  return (
    <div>
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-xl font-semibold tracking-tight text-fg">Обзор</h1>
        <p className="min-w-0 break-all text-sm text-fg-muted">
          {profile.data?.email ?? cachedEmail ?? ''}
        </p>
      </header>

      {kyc.data ? <VerificationPrompt kyc={kyc.data} variant="page" className="mt-6" /> : null}

      {/* The most-used control in the product, and the only thing that outranks
          it is the prompt that says the account cannot earn at all yet. */}
      <TrafficPanel className="mt-6" />

      {/* The one dominant figure on the screen, and the route to Финансы. */}
      <section aria-labelledby="balance-heading" className="mt-8">
        <h2 id="balance-heading" className="sr-only">
          Баланс
        </h2>
        <BalancesPanel variant="overview" aside={earned} />
      </section>

      {/* Compact stat strip — four numbers, one hairline frame. Two-by-two until
          768px: at 640px four columns give each label 150px, and «Ожидают
          подтверждения» then runs to three cramped lines. */}
      <SectionHeading>Сегодня</SectionHeading>
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-line bg-line md:grid-cols-4">
        <div className="bg-surface">
          <Stat
            label="Ожидают подтверждения"
            value={String(pendingCount)}
            tone={pendingCount > 0 ? 'warn' : 'neutral'}
            href={
              pendingCount > 0 ? (
                <Link
                  to="/traffic"
                  className="focus-ring tap-target inline-flex rounded-sm text-fg-accent underline underline-offset-2"
                >
                  Открыть трафик
                </Link>
              ) : undefined
            }
          />
        </div>
        <div className="bg-surface">
          <Stat label="Подтверждено сегодня" value={String(confirmedToday)} />
        </div>
        <div className="bg-surface">
          <Stat
            label="Открытые споры"
            value={String(openDisputes)}
            tone={openDisputes > 0 ? 'danger' : 'neutral'}
          />
        </div>
        <div className="bg-surface">
          <Stat label="Проверенные карты" value={String(verifiedCards)} />
        </div>
      </div>

      {/* Recent activity */}
      <SectionHeading
        actions={
          <Link
            to="/traffic"
            className="focus-ring tap-target inline-flex rounded-sm text-sm font-medium text-fg-accent underline underline-offset-2"
          >
            Все платежи
          </Link>
        }
      >
        Последние операции
      </SectionHeading>

      <Panel padding="none">
        <PanelHeader
          title={`Последние ${RECENT_LIMIT} ${plural(RECENT_LIMIT, NOUN.transaction)}`}
          description="Сначала новые. Сумма — то, что отправил мерчант; «ваша комиссия» — то, что осталось вам."
        />

        {transactions.isPending ? (
          <TableSkeleton rows={6} columns={5} />
        ) : transactions.isError ? (
          <div className="p-4">
            <InlineError
              message={toMessage(transactions.error, 'Не удалось загрузить транзакции.')}
              onRetry={() => void transactions.refetch()}
            />
          </div>
        ) : recent.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="Транзакций пока нет"
            description={
              kyc.data?.canReceiveTraffic
                ? 'Аккаунт активен. Первый платёж появится здесь и на экране «Трафик», как только мерчант направит его вам.'
                : 'Платежи начнут приходить, когда верификация будет одобрена и хотя бы одна карта пройдёт проверку.'
            }
          />
        ) : (
          <ActivityList transactions={recent} />
        )}
      </Panel>
    </div>
  );
}

function countConfirmedToday(transactions: Array<{ status: string; confirmedAt: string | null }>): number {
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const boundary = startOfDay.getTime();

  return transactions.filter((tx) => {
    if (!tx.confirmedAt) return false;
    const at = Date.parse(tx.confirmedAt);
    return !Number.isNaN(at) && at >= boundary;
  }).length;
}
